import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Download, Search, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const STATUS_LABELS = {
  new: { label: "Nouveau", cls: "bg-slate-100 text-slate-700" },
  nouveau: { label: "Nouveau", cls: "bg-slate-100 text-slate-700" },
  en_cours: { label: "En cours", cls: "bg-amber-100 text-amber-700" },
  traite: { label: "Traité", cls: "bg-emerald-100 text-emerald-700" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_LABELS[status] || STATUS_LABELS.nouveau;
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>
      {s.label}
    </span>
  );
};

const PAGE_SIZE = 20;

const exportCSV = (rows) => {
  if (!rows.length) return;
  const mapped = rows.map((q) => ({
    id: q.id,
    date: q.created_at,
    nom: q.data?.name || `${q.data?.first_name || ""} ${q.data?.last_name || ""}`,
    email: q.data?.email || "",
    telephone: q.data?.phone || "",
    service: q.data?.service_category || "",
    surface: q.data?.surface || "",
    estimation_min: q.estimate_min ? Math.round(q.estimate_min) : "",
    estimation_max: q.estimate_max ? Math.round(q.estimate_max) : "",
    statut: q.status || "nouveau",
  }));
  const headers = Object.keys(mapped[0]);
  const lines = [
    headers.join(";"),
    ...mapped.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(";")),
  ];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "devis-bionet.csv";
  a.click();
  URL.revokeObjectURL(url);
};

const AdminQuotesPage = () => {
  const nav = useNavigate();
  const { quotes, updateQuoteStatus, deleteQuote } = useAdmin();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [periodFilter, setPeriodFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [confirmId, setConfirmId] = useState(null);

  const filtered = useMemo(() => {
    const now = new Date();
    return quotes.filter((q) => {
      if (periodFilter !== "all") {
        const days = periodFilter === "7j" ? 7 : 30;
        if ((now - new Date(q.created_at)) / 86400000 > days) return false;
      }
      const qs = q.status || "nouveau";
      if (statusFilter !== "all" && qs !== statusFilter) return false;
      if (search) {
        const t = search.toLowerCase();
        const name = (q.data?.name || `${q.data?.first_name || ""} ${q.data?.last_name || ""}`).toLowerCase();
        const email = (q.data?.email || "").toLowerCase();
        if (!name.includes(t) && !email.includes(t)) return false;
      }
      return true;
    });
  }, [quotes, search, statusFilter, periodFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => setPage(1);

  const handleDelete = async () => {
    if (!confirmId) return;
    await deleteQuote(confirmId);
    setConfirmId(null);
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Nom, email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); reset(); }}
            className="pl-9"
            data-testid="quotes-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); reset(); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-green"
          data-testid="quotes-status-filter"
        >
          <option value="all">Tous statuts</option>
          <option value="nouveau">Nouveau</option>
          <option value="en_cours">En cours</option>
          <option value="traite">Traité</option>
        </select>
        <select
          value={periodFilter}
          onChange={(e) => { setPeriodFilter(e.target.value); reset(); }}
          className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-brand-green"
          data-testid="quotes-period-filter"
        >
          <option value="all">Toutes périodes</option>
          <option value="7j">7 derniers jours</option>
          <option value="30j">30 derniers jours</option>
        </select>
        <Button variant="outline" className="rounded-full text-sm ml-auto" onClick={() => exportCSV(filtered)} data-testid="export-quotes-csv">
          <Download className="h-4 w-4 mr-2" /> Exporter CSV
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left text-xs font-semibold uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Surface</th>
              <th className="px-4 py-3">Estimation</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Modifier</th>
              <th className="px-4 py-3" />
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pageItems.map((q) => (
              <tr
                key={q.id}
                className="hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => nav(`/admin/devis/${q.id}`)}
                data-testid={`quote-row-${q.id}`}
              >
                <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                  {new Date(q.created_at).toLocaleDateString("fr-FR")}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">
                    {q.data?.name || `${q.data?.first_name || ""} ${q.data?.last_name || ""}`.trim()}
                  </p>
                  <p className="text-xs text-slate-400">{q.data?.email}</p>
                </td>
                <td className="px-4 py-3 text-slate-600">{q.data?.service_category || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{q.data?.surface || "—"}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                  {q.estimate_min && q.estimate_max
                    ? `${Math.round(q.estimate_min)} – ${Math.round(q.estimate_max)} €`
                    : "—"}
                </td>
                <td className="px-4 py-3"><StatusBadge status={q.status || "nouveau"} /></td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <select
                    value={q.status || "nouveau"}
                    onChange={(e) => updateQuoteStatus(q.id, e.target.value)}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-brand-green"
                    data-testid={`quote-status-${q.id}`}
                  >
                    <option value="nouveau">Nouveau</option>
                    <option value="en_cours">En cours</option>
                    <option value="traite">Traité</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-slate-300 text-base">→</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setConfirmId(q.id)}
                    className="text-slate-300 hover:text-red-500 transition-colors"
                    data-testid={`delete-quote-${q.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-slate-400">Aucun devis pour ces filtres.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-500">
          <span>{filtered.length} devis -page {page}/{totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer ce devis ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Cette action est irréversible.</p>
          <div className="flex gap-3 justify-end mt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmId(null)}>Annuler</Button>
            <Button className="rounded-full bg-red-500 hover:bg-red-600 text-white" onClick={handleDelete} data-testid="confirm-delete-quote">
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotesPage;
